import React, { useState, useCallback, Suspense } from 'react';
import './App.css';
import GalleryScene from './components/GalleryScene';
import DoorTransition from './components/DoorTransition';
import InactivityPopup from './components/InactivityPopup';
import { ARTWORKS } from './data/artworks';

// ── Lazy imports (composants secondaires non affichés au démarrage) ──
const SecondRoom           = React.lazy(() => import('./components/SecondRoom'));
const ArtworkDetailOverlay = React.lazy(() => import('./components/ArtworkDetailOverlay'));
const FloatingNav          = React.lazy(() => import('./components/FloatingNav'));
const AudioSystem          = React.lazy(() => import('./components/AudioSystem'));
const ContactSection       = React.lazy(() => import('./components/ContactSection'));
const UniverseSection      = React.lazy(() => import('./components/UniverseSection'));
const GuestBook            = React.lazy(() => import('./components/GuestBook'));
const ArtistPage           = React.lazy(() => import('./components/ArtistPage'));
const AdminDashboard       = React.lazy(() => import('./components/AdminDashboard'));
const AcquireForm          = React.lazy(() => import('./components/AcquireForm'));

// Read URL params once on load
const _params = new URLSearchParams(window.location.search);
const _initialArtworkId = _params.get('artwork') || null;
const _isAdmin = _params.get('admin') === '1';

function App() {
  const [view, setView] = useState(_isAdmin ? 'admin' : 'gallery');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [isDetailMode, setIsDetailMode] = useState(false);
  const [transitionActive, setTransitionActive] = useState(false);
  const [showDoor, setShowDoor] = useState(!_isAdmin);
  const [showContact, setShowContact]     = useState(false);
  const [showUniverse, setShowUniverse]   = useState(false);
  const [showGuestbook, setShowGuestbook] = useState(false);
  const [showArtist, setShowArtist]       = useState(false);
  const [showAcquire, setShowAcquire]     = useState(false);
  const [acquireArtwork, setAcquireArtwork] = useState(null);
  const [acquireType, setAcquireType]     = useState('original');
  const openAcquire = useCallback((artwork, type = 'original') => {
    setAcquireArtwork(artwork); setAcquireType(type); setShowAcquire(true);
  }, []);
  const closeAcquire = useCallback(() => setShowAcquire(false), []);
  const [language, setLanguage] = useState('fr');
  const [pendingArtworkId] = useState(_initialArtworkId);
  const [skipGalleryIntro, setSkipGalleryIntro] = useState(false);
  const [galleryReset, setGalleryReset] = useState(0);

  const enterGallery = useCallback(() => {
    setShowDoor(true);
    setTimeout(() => setView('gallery'), 350);
  }, []);

  const enterSecondRoom = useCallback(() => {
    setShowDoor(true);
    setTimeout(() => setView('second-room'), 350);
  }, []);

  const leaveSecondRoom = useCallback(() => {
    setSkipGalleryIntro(true);
    setShowDoor(true);
    setTimeout(() => {
      setView('gallery');
      setIsDetailMode(false);
      setSelectedArtwork(null);
      setTimeout(() => setSkipGalleryIntro(false), 800);
    }, 350);
  }, []);

  const handleDoorComplete = useCallback(() => {
    setShowDoor(false);
  }, []);

  const handleArtworkSelect = useCallback((artwork) => {
    setSelectedArtwork(artwork);
  }, []);

  // Test helper: expose artwork opener for automated testing (dev only)
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      window.__testOpenArtwork = (artworkOrId) => {
        const artwork = typeof artworkOrId === 'string'
          ? ARTWORKS.find(a => a.id === artworkOrId)
          : artworkOrId;
        setSelectedArtwork(artwork); setIsDetailMode(true);
      };
      return () => { delete window.__testOpenArtwork; };
    }
  }, []);

  const handleCameraArrived = useCallback(() => {
    setTimeout(() => setIsDetailMode(true), 200);
  }, []);

  const closeDetail = useCallback(() => {
    setIsDetailMode(false);
    setSelectedArtwork(null);
  }, []);

  const handleReturnToGalleryStart = useCallback(() => {
    setIsDetailMode(false);
    setSelectedArtwork(null);
  }, []);

  const handleInactivityArtworkView = useCallback((artwork) => {
    setSelectedArtwork(artwork);
    setIsDetailMode(true);
  }, []);

  const handleNavAction = useCallback((action) => {
    if (action === 'gallery') {
      if (view === 'hero') enterGallery();
      else { setIsDetailMode(false); setSelectedArtwork(null); }
    } else if (action === 'contact') {
      setShowContact(true);
    } else if (action === 'universe') {
      setShowUniverse(true);
    } else if (action === 'guestbook') {
      setShowGuestbook(true);
    } else if (action === 'artist') {
      setShowArtist(true);
    } else if (action === 'home') {
      if (view === 'gallery') {
        setTransitionActive(true);
        setIsDetailMode(false);
        setSelectedArtwork(null);
        setGalleryReset(k => k + 1);
        setTimeout(() => setTransitionActive(false), 700);
      } else if (view === 'second-room') {
        leaveSecondRoom();
      }
    }
  }, [view, enterGallery, leaveSecondRoom]);

  // Admin view
  if (view === 'admin') {
    return (
      <div className="shiratseva-app" data-testid="shiratseva-app">
        <Suspense fallback={null}><AdminDashboard /></Suspense>
      </div>
    );
  }

  return (
    <div className="shiratseva-app" data-testid="shiratseva-app">
      <div className="grain-overlay" aria-hidden="true" />


      {view === 'gallery' && (
        <div style={showUniverse ? { visibility: 'hidden', pointerEvents: 'none' } : {}}>
          <GalleryScene
            selectedArtwork={selectedArtwork}
            onCameraArrived={handleCameraArrived}
            isDetailMode={isDetailMode}
            onArtworkSelect={handleArtworkSelect}
            language={language}
            onReturnToStart={handleReturnToGalleryStart}
            onOpenGuestbook={() => setShowGuestbook(true)}
            pendingArtworkId={pendingArtworkId}
            onEnterSecondRoom={enterSecondRoom}
            skipIntro={skipGalleryIntro}
            overlayOpen={showGuestbook || showContact || showUniverse || showArtist}
            galleryReset={galleryReset}
          />
        </div>
      )}

      {view === 'second-room' && (
        <Suspense fallback={null}>
          <SecondRoom onReturn={leaveSecondRoom} language={language} />
        </Suspense>
      )}

      {isDetailMode && selectedArtwork && (
        <Suspense fallback={null}>
          <ArtworkDetailOverlay
            artwork={selectedArtwork}
            onClose={closeDetail}
            language={language}
            onAcquire={openAcquire}
          />
        </Suspense>
      )}

      {view === 'gallery' && !showDoor && !showUniverse && (
        <Suspense fallback={null}>
          <FloatingNav view={view} isDetailMode={isDetailMode} onAction={handleNavAction} language={language} setLanguage={setLanguage} />
        </Suspense>
      )}

      {showContact && (
        <Suspense fallback={null}>
          <ContactSection onClose={() => setShowContact(false)} language={language} />
        </Suspense>
      )}
      {showUniverse && (
        <Suspense fallback={null}>
          <UniverseSection onClose={() => setShowUniverse(false)} language={language} />
        </Suspense>
      )}
      {showGuestbook && (
        <Suspense fallback={null}>
          <GuestBook onClose={() => setShowGuestbook(false)} language={language} />
        </Suspense>
      )}
      {showArtist && (
        <Suspense fallback={null}>
          <ArtistPage onClose={() => setShowArtist(false)} language={language} />
        </Suspense>
      )}

      <InactivityPopup onViewArtwork={handleInactivityArtworkView} language={language} />

      {showAcquire && (
        <Suspense fallback={null}>
          <AcquireForm
            artwork={acquireArtwork}
            type={acquireType}
            onClose={closeAcquire}
          />
        </Suspense>
      )}

      {(view === 'gallery' || view === 'second-room') && !showUniverse && (
        <Suspense fallback={null}><AudioSystem /></Suspense>
      )}

      <DoorTransition isVisible={showDoor} onComplete={handleDoorComplete} />

      <div className={`transition-veil${transitionActive ? ' active' : ''}`} aria-hidden="true" />
    </div>
  );
}

export default App;
